import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { channelIsActive, toApiError, useApi } from '@/shared/api';
import { propertyKeys } from '../queries';
import { normalizeChannel } from '../OtaLogo';

export const channelsQueryKey = ['channex', 'channels'] as const;

interface Options {
  /** Nur ein WorkspaceAdmin darf die Kanalliste überhaupt lesen (Server: ChannexConnections). */
  manages: boolean;
  onIframeUrl: (url: string) => void;
  onDisconnected: () => void;
}

/**
 * Die Daten des Kanäle-Reiters: Apartment, verbundene Kanäle, letzte Buchungen, letzte
 * Übertragung — plus die beiden Mutationen. Steht getrennt von der Fläche, damit dort nur noch
 * Darstellung liegt.
 */
export function useChannelsTab(id: string, { manages, onIframeUrl, onDisconnected }: Options) {
  const api = useApi();
  const qc = useQueryClient();

  const { data: property } = useQuery({
    queryKey: propertyKeys.detail(id),
    queryFn: () => api.properties.get(id),
  });

  const channels = useQuery({
    queryKey: channelsQueryKey,
    queryFn: () => api.channex.listChannels(),
    enabled: !!property?.channexPropertyId && manages,
  });
  const channelsData = channels.data;

  const ownedChannels =
    channelsData?.data.filter((c) =>
      c.attributes.properties?.includes(property?.channexPropertyId ?? ''),
    ) ?? [];
  const activeChannels = ownedChannels.filter(channelIsActive);
  const bookingComConnected = ownedChannels.some((c) => c.attributes.channel === 'BookingCom');
  const airbnbConnected = ownedChannels.some(
    (c) => normalizeChannel(c.attributes.channel) === 'Airbnb',
  );

  const { data: lastBookingsData } = useQuery({
    queryKey: ['channex', 'lastBookings', id],
    queryFn: () => api.channex.lastBookingsPerOta(id),
    enabled: !!property?.channexPropertyId,
    staleTime: 30_000,
  });
  const lastBookingByOta = new Map(
    (lastBookingsData?.items ?? []).map((it) => [normalizeChannel(it.otaName), it]),
  );
  const mostRecentBooking = (lastBookingsData?.items ?? [])
    .filter((it) => it.receivedAt)
    .sort((a, b) => (a.receivedAt < b.receivedAt ? 1 : -1))[0];

  const { data: syncActivity } = useQuery({
    queryKey: ['channex', 'syncActivity', id],
    queryFn: () => api.channex.syncActivity(id),
    enabled: !!property?.channexPropertyId,
    staleTime: 30_000,
  });
  const lastAriPush = syncActivity?.recent.find((e) => e.source === 'ari-push');

  const openMapping = useMutation({
    mutationFn: () => api.channex.createIframeSession(property!.channexPropertyId!, 'channels'),
    onSuccess: (session) => onIframeUrl(session.iframeUrl),
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(e.detail ?? e.title);
    },
  });

  const disconnect = useMutation({
    mutationFn: (channelId: string) => api.channex.deleteChannel(channelId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: channelsQueryKey });
      onDisconnected();
      toast.success('Kanal für das gesamte Objekt getrennt');
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(e.detail ?? e.title);
    },
  });

  return {
    property,
    isLoading: channels.isLoading,
    // Was verbunden ist, sagt erst eine frisch geladene Liste. Bis dahin — und nach einem
    // Ladefehler — bietet der Reiter weder Verbinden noch Mapping an: sonst entstünde etwa eine
    // zweite Booking.com-Verbindung für dasselbe Objekt.
    channelsKnown: channels.isSuccess,
    channelsError: channels.isError ? channels.error : null,
    retryChannels: () => void channels.refetch(),
    ownedChannels,
    activeChannels,
    bookingComConnected,
    airbnbConnected,
    lastBookingByOta,
    mostRecentBooking,
    lastAriPush,
    openMapping,
    disconnect,
  };
}
